import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { Plus, Phone, Mail, IndianRupee, ArrowRight, Trash2, X, ThumbsDown, MapPin, Calendar, User } from 'lucide-react';
import { API } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useNavigate } from 'react-router-dom';

// Pipeline and secondary stages configuration
const PIPELINE_STAGES = [
  { value: 'new', label: 'New', color: 'bg-stone-100 border-stone-300' },
  { value: 'contacted', label: 'Contacted', color: 'bg-blue-50 border-blue-300' },
  { value: 'qualified', label: 'Qualified', color: 'bg-purple-50 border-purple-300' },
  { value: 'proposal_sent', label: 'Proposal Sent', color: 'bg-amber-50 border-amber-300' },
];

const SECONDARY_STAGES = [
  { value: 'negotiation', label: 'Negotiation', color: 'bg-orange-50 border-orange-300', icon: '🤝' },
  { value: 'won', label: 'Won', color: 'bg-green-50 border-green-300', icon: '🎉' },
  { value: 'lost', label: 'Lost', color: 'bg-red-50 border-red-300', icon: '❌' },
];

// LeadCard component moved outside main component
const LeadCard = ({ lead, stage, showActions = true, onMarkLost, onDelete, onMoveForward, onConvert, onReactivate }) => (
  <div
    className={`${stage.color} border rounded-xl p-4 hover:shadow-md transition-all group`}
    data-testid={`lead-card-${lead.id}`}
  >
    <div className="flex justify-between items-start mb-3">
      <h4 className="font-semibold text-stone-900">{lead.name}</h4>
      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {lead.stage !== 'lost' && lead.stage !== 'won' && (
          <button
            onClick={(e) => { e.stopPropagation(); onMarkLost(lead); }}
            className="p-1.5 text-orange-500 hover:bg-orange-100 rounded"
            title="Mark as Lost"
          >
            <ThumbsDown size={14} />
          </button>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(lead); }}
          className="p-1.5 text-red-500 hover:bg-red-100 rounded"
          title="Delete Lead"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
    
    <div className="space-y-1.5 text-sm text-stone-600">
      <div className="flex items-center gap-2">
        <Phone size={14} className="text-stone-400" />
        <span>{lead.phone}</span>
      </div>
      {lead.email && (
        <div className="flex items-center gap-2">
          <Mail size={14} className="text-stone-400" />
          <span className="truncate">{lead.email}</span>
        </div>
      )}
      {lead.location && (
        <div className="flex items-center gap-2">
          <MapPin size={14} className="text-stone-400" />
          <span>{lead.location}</span>
        </div>
      )}
      {lead.budget && (
        <div className="flex items-center gap-2">
          <IndianRupee size={14} className="text-stone-400" />
          <span className="font-medium">₹{lead.budget.toLocaleString()}</span>
        </div>
      )}
    </div>

    {showActions && (
      <div className="flex gap-2 mt-4 pt-3 border-t border-stone-200">
        {lead.stage !== 'won' && lead.stage !== 'lost' && lead.stage !== 'negotiation' && (
          <button
            onClick={() => onMoveForward(lead)}
            className="flex-1 text-xs bg-primary text-white px-3 py-2 rounded-lg hover:bg-primary/90 transition-colors flex items-center justify-center gap-1"
          >
            Move Forward <ArrowRight size={12} />
          </button>
        )}
        {lead.stage === 'negotiation' && (
          <>
            <button
              onClick={() => onMoveForward(lead, 'won')}
              className="flex-1 text-xs bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 transition-colors"
            >
              Won 🎉
            </button>
            <button
              onClick={() => onMarkLost(lead)}
              className="flex-1 text-xs bg-red-600 text-white px-3 py-2 rounded-lg hover:bg-red-700 transition-colors"
            >
              Lost
            </button>
          </>
        )}
        {lead.stage === 'won' && (
          <button
            onClick={() => onConvert(lead.id)}
            className="flex-1 text-xs bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 transition-colors"
          >
            Convert to Project
          </button>
        )}
        {lead.stage === 'lost' && (
          <button
            onClick={() => onReactivate(lead.id)}
            className="flex-1 text-xs bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Reactivate → Negotiation
          </button>
        )}
      </div>
    )}
  </div>
);

export default function Leads() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [lostDialogOpen, setLostDialogOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [lostReason, setLostReason] = useState('');
  const [activeTab, setActiveTab] = useState('pipeline');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    source: 'website',
    stage: 'new',
    budget: '',
    requirements: '',
    location: '',
  });

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${API}/leads`);
      setLeads(response.data);
    } catch (error) {
      if (error.response?.status !== 402) toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/leads`, {
        ...formData,
        budget: formData.budget ? parseFloat(formData.budget) : null,
      });
      toast.success('Lead created successfully');
      setDialogOpen(false);
      fetchLeads();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create lead');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      source: 'website',
      stage: 'new',
      budget: '',
      requirements: '',
      location: '',
    });
  };

  const moveLeadToStage = async (leadId, newStage) => {
    try {
      await axios.put(`${API}/leads/${leadId}/stage?stage=${newStage}`);
      fetchLeads();
      toast.success('Lead stage updated');
    } catch (error) {
      toast.error('Failed to update lead stage');
    }
  };

  const markAsLost = async () => {
    if (!selectedLead) return;
    try {
      await axios.put(`${API}/leads/${selectedLead.id}/stage?stage=lost`);
      toast.success('Lead marked as lost');
      setLostDialogOpen(false);
      setSelectedLead(null);
      setLostReason('');
      fetchLeads();
    } catch (error) {
      toast.error('Failed to update lead');
    }
  };

  const deleteLead = async () => {
    if (!selectedLead) return;
    try {
      await axios.delete(`${API}/leads/${selectedLead.id}`);
      toast.success('Lead deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedLead(null);
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete lead');
    }
  };

  const convertToProject = async (leadId) => {
    try {
      const response = await axios.post(`${API}/leads/${leadId}/convert-to-project`);
      toast.success('Lead converted to project!');
      navigate(`/projects/${response.data.project_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert lead');
    }
  };

  const getLeadsByStage = (stage) => {
    return leads.filter((lead) => lead.stage === stage);
  };

  const getPipelineLeads = () => {
    return leads.filter((lead) => ['new', 'contacted', 'qualified', 'proposal_sent'].includes(lead.stage));
  };

  // Handlers for LeadCard callbacks
  const handleMarkLost = (lead) => {
    setSelectedLead(lead);
    setLostDialogOpen(true);
  };

  const handleDeleteClick = (lead) => {
    setSelectedLead(lead);
    setDeleteDialogOpen(true);
  };

  const handleMoveForward = (lead, targetStage = null) => {
    if (targetStage) {
      moveLeadToStage(lead.id, targetStage);
    } else {
      const allStages = [...PIPELINE_STAGES, ...SECONDARY_STAGES];
      const currentIndex = allStages.findIndex((s) => s.value === lead.stage);
      if (currentIndex < allStages.length - 2) {
        moveLeadToStage(lead.id, allStages[currentIndex + 1].value);
      }
    }
  };

  const handleReactivate = (leadId) => {
    moveLeadToStage(leadId, 'negotiation');
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64" data-testid="loading-state">
          <div className="text-stone-400">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="leads-page" className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-manrope text-3xl md:text-5xl font-bold tracking-tight text-stone-900 mb-2" data-testid="leads-title">
              Lead Management
            </h1>
            <p className="text-stone-500">Track your sales pipeline from inquiry to project</p>
          </div>
          <Button onClick={() => setDialogOpen(true)} className="apple-button" data-testid="create-lead-button">
            <Plus size={18} className="mr-2" />
            New Lead
          </Button>
        </div>

        {/* Tabs Row */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-stone-100 p-1 h-auto flex-wrap">
            <TabsTrigger value="pipeline" className="text-stone-600 data-[state=active]:bg-white data-[state=active]:text-stone-900">
              Pipeline ({getPipelineLeads().length})
            </TabsTrigger>
            <TabsTrigger value="negotiation" className="text-stone-600 data-[state=active]:bg-white data-[state=active]:text-stone-900">
              🤝 Negotiation ({getLeadsByStage('negotiation').length})
            </TabsTrigger>
            <TabsTrigger value="won" className="text-stone-600 data-[state=active]:bg-white data-[state=active]:text-stone-900">
              🎉 Won ({getLeadsByStage('won').length})
            </TabsTrigger>
            <TabsTrigger value="lost" className="text-stone-600 data-[state=active]:bg-white data-[state=active]:text-stone-900">
              ❌ Lost ({getLeadsByStage('lost').length})
            </TabsTrigger>
          </TabsList>

          {/* Pipeline View */}
          <TabsContent value="pipeline" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kanban-board">
              {PIPELINE_STAGES.map((stage) => {
                const stageLeads = getLeadsByStage(stage.value);
                return (
                  <div key={stage.value} className="apple-card p-4" data-testid={`stage-${stage.value}`}>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-stone-900">{stage.label}</h3>
                      <span className="bg-stone-100 px-2 py-1 rounded text-sm font-medium">{stageLeads.length}</span>
                    </div>
                    <div className="space-y-3">
                      {stageLeads.map((lead) => (
                        <LeadCard 
                          key={lead.id} 
                          lead={lead} 
                          stage={stage}
                          onMarkLost={handleMarkLost}
                          onDelete={handleDeleteClick}
                          onMoveForward={handleMoveForward}
                          onConvert={convertToProject}
                          onReactivate={handleReactivate}
                        />
                      ))}
                      {stageLeads.length === 0 && (
                        <div className="text-center text-stone-400 text-sm py-8 border-2 border-dashed border-stone-200 rounded-lg">
                          No leads
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* Negotiation View */}
          <TabsContent value="negotiation" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {getLeadsByStage('negotiation').map((lead) => (
                <LeadCard 
                  key={lead.id} 
                  lead={lead} 
                  stage={SECONDARY_STAGES[0]}
                  onMarkLost={handleMarkLost}
                  onDelete={handleDeleteClick}
                  onMoveForward={handleMoveForward}
                  onConvert={convertToProject}
                  onReactivate={handleReactivate}
                />
              ))}
              {getLeadsByStage('negotiation').length === 0 && (
                <div className="col-span-full apple-card p-12 text-center">
                  <p className="text-stone-500">No leads in negotiation</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Won View */}
          <TabsContent value="won" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {getLeadsByStage('won').map((lead) => (
                <LeadCard 
                  key={lead.id} 
                  lead={lead} 
                  stage={SECONDARY_STAGES[1]}
                  onMarkLost={handleMarkLost}
                  onDelete={handleDeleteClick}
                  onMoveForward={handleMoveForward}
                  onConvert={convertToProject}
                  onReactivate={handleReactivate}
                />
              ))}
              {getLeadsByStage('won').length === 0 && (
                <div className="col-span-full apple-card p-12 text-center">
                  <p className="text-stone-500">No won leads yet</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Lost View */}
          <TabsContent value="lost" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {getLeadsByStage('lost').map((lead) => (
                <LeadCard 
                  key={lead.id} 
                  lead={lead} 
                  stage={SECONDARY_STAGES[2]} 
                  showActions={true}
                  onMarkLost={handleMarkLost}
                  onDelete={handleDeleteClick}
                  onMoveForward={handleMoveForward}
                  onConvert={convertToProject}
                  onReactivate={handleReactivate}
                />
              ))}
              {getLeadsByStage('lost').length === 0 && (
                <div className="col-span-full apple-card p-12 text-center">
                  <p className="text-stone-500">No lost leads</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Create Lead Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl" data-testid="create-lead-dialog">
            <DialogHeader>
              <DialogTitle className="font-manrope text-xl">Create New Lead</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Name *</Label>
                  <Input
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    data-testid="name-input"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Phone *</Label>
                  <Input
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    data-testid="phone-input"
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="email-input"
                  className="mt-1"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Source</Label>
                  <Select
                    value={formData.source}
                    onValueChange={(value) => setFormData({ ...formData, source: value })}
                  >
                    <SelectTrigger data-testid="source-select" className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="website">Website</SelectItem>
                      <SelectItem value="referral">Referral</SelectItem>
                      <SelectItem value="social_media">Social Media</SelectItem>
                      <SelectItem value="walk_in">Walk-in</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Budget (₹)</Label>
                  <Input
                    type="number"
                    value={formData.budget}
                    onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                    data-testid="budget-input"
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label>Location</Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  data-testid="location-input"
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Requirements</Label>
                <Textarea
                  rows={3}
                  value={formData.requirements}
                  onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
                  data-testid="requirements-input"
                  className="mt-1"
                />
              </div>

              <div className="flex gap-3 justify-end pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="apple-button" data-testid="submit-lead-button">
                  Create Lead
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Mark as Lost Dialog */}
        <AlertDialog open={lostDialogOpen} onOpenChange={setLostDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <ThumbsDown className="text-orange-500" size={20} />
                Mark Lead as Lost
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-4">
                  <p>Are you sure you want to mark <strong>{selectedLead?.name}</strong> as lost?</p>
                  <div>
                    <Label>Reason (Optional)</Label>
                    <Textarea
                      placeholder="Why was this lead lost?"
                      value={lostReason}
                      onChange={(e) => setLostReason(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => { setLostReason(''); setSelectedLead(null); }}>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={markAsLost} className="bg-orange-600 hover:bg-orange-700">
                Mark as Lost
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete Lead Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <Trash2 className="text-red-500" size={20} />
                Delete Lead
              </AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete <strong>{selectedLead?.name}</strong>? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setSelectedLead(null)}>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={deleteLead} className="bg-red-600 hover:bg-red-700">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
